import { config } from '@/utilities/config'
import { establishPatientSessionExpiration } from '@/utilities/auth/expireBrowserSession'
import {
  beginPatientSessionVerification,
  classifyPatientApiFailure,
  endPatientSessionVerification,
  isPatientSessionExpired,
} from '@/utilities/auth/patientSession'
import { isAuthPagePath } from '@/utilities/paths'
import { isClientPortal, STYTCH_ACTIVE_SESSION_DURATION } from '@/utilities/stytch/config'
import { getStytchSessionTokens } from '@/utilities/stytch/getStytchJWT'
import { isImpersonationHandoffInProgress } from '@/utilities/stytch/impersonationHandoff'
import { getStytchHttpStatus, isStytchSessionAlreadyGone } from '@/utilities/stytch/onError'

export type PatientSessionVerification = 'valid' | 'invalid' | 'unknown'

export type PatientApiFailureHandleResult =
  | { outcome: 'ignored' }
  | { outcome: 'unchanged' }
  | { outcome: 'session-ended' }
  | { outcome: 'session-refreshed' }

let verificationInFlight: Promise<PatientSessionVerification> | null = null

/**
 * Confirms whether the browser still has a Stytch session.
 * Uses session cookies so a locally invalid JWT can still call `session.authenticate`.
 * A network failure stays unknown so the original request can surface its error.
 */
export async function verifyPatientStytchSession(): Promise<PatientSessionVerification> {
  verificationInFlight ??= verifyPatientStytchSessionOnce().finally(() => {
    verificationInFlight = null
  })
  return verificationInFlight
}

async function verifyPatientStytchSessionOnce(): Promise<PatientSessionVerification> {
  if (!getStytchSessionTokens()) return 'invalid'

  try {
    const { stytchClient } = await import('@/utilities/stytch/stytchClient')
    const response = await stytchClient.session.authenticate({
      session_duration_minutes: STYTCH_ACTIVE_SESSION_DURATION,
    })
    return response?.member ? 'valid' : 'invalid'
  } catch (error) {
    if (isStytchSessionAlreadyGone(error)) return 'invalid'
    const status = getStytchHttpStatus(error)
    if (status === 401 || status === 404) return 'invalid'
    return 'unknown'
  }
}

function shouldIgnorePatientApiFailure(): boolean {
  if (typeof window === 'undefined') return true
  if (isClientPortal()) return true
  if (isAuthPagePath(window.location.pathname)) return true
  // Anonymous visitors on public pages (for example, a kit correction link) have no session to expire.
  if (!getStytchSessionTokens() && config.isPublicPatientPath(window.location.pathname)) {
    return true
  }
  if (isImpersonationHandoffInProgress()) return true
  if (isPatientSessionExpired()) return true
  return false
}

/**
 * Applies a patient Portal API failure to the shared session.
 * Clear authentication failures end the session before the caller receives the error.
 * Ambiguous failures verify with Stytch first; a valid session lets the caller retry the request.
 */
export async function handlePatientApiFailure(
  error: unknown,
): Promise<PatientApiFailureHandleResult> {
  if (shouldIgnorePatientApiFailure()) return { outcome: 'ignored' }

  const action = classifyPatientApiFailure(error)
  if (action === 'stay-on-page') return { outcome: 'unchanged' }

  if (action === 'end-session') {
    establishPatientSessionExpiration()
    return { outcome: 'session-ended' }
  }

  beginPatientSessionVerification()
  try {
    const verification = await verifyPatientStytchSession()
    if (verification === 'valid') {
      return { outcome: 'session-refreshed' }
    }
    if (verification === 'invalid' && !shouldIgnorePatientApiFailure()) {
      establishPatientSessionExpiration()
      return { outcome: 'session-ended' }
    }
    return { outcome: 'unchanged' }
  } finally {
    endPatientSessionVerification()
  }
}
